import { esperaConferencia, precisaDeCategoria } from '@domain/finance'
import type { Category, Transaction } from '@domain/finance'
import type { Cents } from '@shared/money/cents'

/**
 * Os filtros da tela de Transações, como função pura.
 *
 * `Docs/04-Design/Telas/Transações.md` lista: categoria (com descendentes),
 * conta ou cartão, método, faixa de valor, busca, e "só não categorizadas".
 * **Tudo na URL** — quem lê e escreve a URL é a rota; aqui só se decide o que
 * cada filtro significa.
 *
 * ## Por que módulo puro, e não `useMemo` dentro da tela
 *
 * Porque a regra que mais custa acertar aqui não é visual: é *"o total exibido
 * respeita os filtros"*, e essa frase da nota vale para **cinco** filtros, não
 * para um. Com a decisão em função, o teste afirma cada regra uma vez, sem
 * montar a árvore de React para descobrir se a faixa de valor usa módulo.
 *
 * ## A faixa de valor é em MÓDULO, e é a única conversão de sinal aqui
 *
 * `amountCents` é negativo numa saída. Uma faixa "de 50 a 200" digitada por
 * alguém quer dizer *entre R$ 50 e R$ 200*, e comparar isso com −15.000 direto
 * daria "sim, é menor que 200" para toda saída do mês. A comparação é em
 * módulo, e a conversão acontece **uma vez**, aqui — em vez de o formulário
 * mandar números negativos, que seria pedir para alguém digitar o sinal certo.
 *
 * ## A busca olha só a DESCRIÇÃO
 *
 * Não a categoria, não o valor. Quem digita "posto" quer a linha que diz
 * "POSTO IPIRANGA"; uma busca que também casasse a categoria devolveria todo
 * Transporte e a pessoa não teria como saber por quê. Sem acento e sem caixa,
 * porque o extrato do banco vem em maiúsculas e ninguém digita assim.
 */

export type PaymentMethod = NonNullable<Transaction['source']['paymentMethod']>

export type TransactionFilters = {
  /** Texto livre, casado contra a descrição. Vazio é "não filtrado". */
  q: string
  /** Id de categoria. Casa a categoria **e os descendentes dela**. */
  categoryId: string | null
  /** Id de conta **ou** de cartão — `source.instrument.id`, que é um só campo. */
  instrumentId: string | null
  method: PaymentMethod | null
  /** Faixa em MÓDULO, em centavos. Ver o cabeçalho. */
  minCents: Cents | null
  maxCents: Cents | null
  /** O principal chamado à ação da tela. */
  onlyUncategorized: boolean
  /**
   * Só as que a tabela de regras categorizou e ninguém conferiu ainda — a fila
   * de conferência da Fase 10. É o irmão do `onlyUncategorized`: um mostra o
   * que falta decidir, o outro o que o app decidiu sozinho.
   */
  onlyAuto: boolean
}

export const NO_FILTERS: TransactionFilters = {
  q: '',
  categoryId: null,
  instrumentId: null,
  method: null,
  minCents: null,
  maxCents: null,
  onlyUncategorized: false,
  onlyAuto: false,
}

/**
 * Sem acento e sem caixa. `NFD` + remoção dos diacríticos é o jeito que não
 * precisa de uma tabela de "á→a" que alguém teria de manter.
 */
export function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

/**
 * A categoria pedida **e toda a descendência dela**.
 *
 * A nota diz "com descendentes, via `categoryPath`", e o caminho por `parentId`
 * chega no mesmo conjunto sem depender de o `path` estar cunhado — e ele **não
 * está** numa categoria recém-criada pelo cliente, porque quem o escreve é a
 * Cloud Function. Filtrar por `path` faria "Mercado", criada há dez segundos,
 * desaparecer do próprio filtro.
 *
 * O `visited` não é zelo: `wouldCreateCycle` existe justamente porque a árvore
 * pode receber um ciclo, e uma descida ingênua aqui travaria a tela.
 */
export function withDescendants(categoryId: string, categories: Category[]): Set<string> {
  const childrenOf = new Map<string, string[]>()
  for (const category of categories) {
    if (category.parentId === null) continue
    const siblings = childrenOf.get(category.parentId) ?? []
    siblings.push(category.id)
    childrenOf.set(category.parentId, siblings)
  }

  const found = new Set<string>([categoryId])
  const queue = [categoryId]
  while (queue.length > 0) {
    const current = queue.shift()
    if (current === undefined) continue
    for (const child of childrenOf.get(current) ?? []) {
      if (found.has(child)) continue
      found.add(child)
      queue.push(child)
    }
  }
  return found
}

/**
 * Quantos filtros estão ligados. É o que decide se a tela mostra a fila de
 * chips — e o que o teste usa para não afirmar "nenhum filtro" contando `null`
 * campo a campo, que é a asserção que envelhece quando um filtro novo entra.
 */
export function activeCount(filters: TransactionFilters): number {
  return [
    filters.q.trim() !== '',
    filters.categoryId !== null,
    filters.instrumentId !== null,
    filters.method !== null,
    filters.minCents !== null,
    filters.maxCents !== null,
    filters.onlyUncategorized,
    filters.onlyAuto,
  ].filter(Boolean).length
}

export function applyFilters(
  transactions: Transaction[],
  filters: TransactionFilters,
  categories: Category[],
): Transaction[] {
  const busca = normalize(filters.q.trim())
  const familia =
    filters.categoryId === null ? null : withDescendants(filters.categoryId, categories)

  return transactions.filter((transaction) => {
    const { source, enrichment } = transaction

    if (busca !== '' && !normalize(source.description).includes(busca)) return false

    if (filters.onlyUncategorized && !precisaDeCategoria(transaction)) return false

    if (filters.onlyAuto && !esperaConferencia(enrichment.categorySource)) return false

    if (familia !== null) {
      // Transação sem categoria não pertence a família nenhuma. Escrever isso
      // explicitamente porque `familia.has(null as never)` seria `false` por
      // acidente, e acidente não é regra.
      if (enrichment.categoryId === null) return false
      if (!familia.has(enrichment.categoryId)) return false
    }

    if (filters.instrumentId !== null && source.instrument.id !== filters.instrumentId) {
      return false
    }

    if (filters.method !== null && source.paymentMethod !== filters.method) return false

    // Em módulo — ver o cabeçalho. `Math.abs` e não `absCents` para não arrastar
    // a dependência de dinheiro para uma comparação que é de grandeza.
    const grandeza = Math.abs(source.amountCents)
    if (filters.minCents !== null && grandeza < filters.minCents) return false
    if (filters.maxCents !== null && grandeza > filters.maxCents) return false

    return true
  })
}
